import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, RowButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';
import { SearchInput } from '@eika/input';

import { useCrossBorderReceivers } from '../../../../fetchHooks/useCrossBorderReceivers';
import useHandleCrossBorderReload from '../../../../hooks/useHandleCrossBorderReload';
import useTranslation from '../../../../hooks/useTranslation';
import { SelectList } from '../../../shared/SelectList';
import { CrossBorderPaymentActionType } from '../form/actions';
import { toSelectCrossBorderReceiver } from '../form/state';
import useFormContext from '../form/useFormContext';
import CrossBorderReceiverListItem from './CrossBorderReceiverListItem';
import NewCrossBorderReceiverListSkeleton from './NewCrossBorderReceiverListSkeleton';

import './NewCrossBorderReceiverList.scss';

const NewCrossBorderReceiverList: React.FC = () => {
    const translated = useTranslation();
    const navigate = useNavigate();
    const [searchString, setSearchString] = useState<string>('');
    const { data: receivers, isLoading, error, refetch } = useCrossBorderReceivers();

    const { handleChange } = useFormContext();
    useHandleCrossBorderReload();

    useEffect(() => {
        const hasEnteredAccountNumber = /^[\d. ]+$/.test(searchString);
        if (!hasEnteredAccountNumber) {
            handleChange({
                type: CrossBorderPaymentActionType.SET_RECEIVER_NAME,
                payload: searchString,
            });
        }
    }, [handleChange, searchString]);

    const filteredReceiverList = receivers?.filter((receiver) => {
        return (
            receiver.name.toLowerCase().includes(searchString.toLowerCase()) ||
            receiver.account.number.toLowerCase().includes(searchString.toLowerCase())
        );
    });

    useEffect(() => {
        if (!isLoading && receivers?.length === 0) {
            handleChange({ type: CrossBorderPaymentActionType.RESET_RECEIVER });
            navigate('/mottaker-land', { replace: true });
        }
    }, [handleChange, navigate, isLoading, receivers]);

    return (
        <>
            <h2 className="select-cross-border-receiver__title">{translated.payTo}</h2>
            <div className="select-cross-border-receiver">
                <SearchInput
                    placeholder={translated.name}
                    value={searchString}
                    onChange={(event) => setSearchString(event.target.value)}
                    className="select-cross-border-receiver__search-input"
                />
                {isLoading && <NewCrossBorderReceiverListSkeleton />}
                {!isLoading && (
                    <div className="select-cross-border-receiver__list">
                        <SelectList>
                            <li key="addNewReceiver">
                                <RowButton
                                    onClick={() => {
                                        handleChange({
                                            type: CrossBorderPaymentActionType.RESET_RECEIVER,
                                        });
                                        navigate('/mottaker-land');
                                    }}
                                    className="select-cross-border-receiver__add-receiver-row"
                                    data-testid="add-new-receiver"
                                >
                                    <span className="add-icon">
                                        <Icon
                                            type={IconTypes.PLUSS_24}
                                            className="icon--24 select--cross-border-receiver__pluss-icon"
                                        />
                                    </span>
                                    <span>{translated.crossBorderPayment.newReceiver.title}</span>
                                </RowButton>
                            </li>
                            {filteredReceiverList &&
                                !error &&
                                filteredReceiverList.map((receiver) => (
                                    <li key={receiver.id.value}>
                                        <CrossBorderReceiverListItem
                                            crossBorderReceiver={receiver}
                                            onClick={() => {
                                                const selectedReceiver = toSelectCrossBorderReceiver(receiver);
                                                handleChange({
                                                    type: CrossBorderPaymentActionType.TO_ACCOUNT_SELECTED,
                                                    payload: selectedReceiver,
                                                });
                                                handleChange({
                                                    type: CrossBorderPaymentActionType.SET_CHOSEN_RECEIVER,
                                                    payload: receiver,
                                                });
                                                navigate('/mottaker');
                                            }}
                                        />
                                    </li>
                                ))}
                        </SelectList>
                    </div>
                )}
                {error && (
                    <ErrorCard
                        className="select-cross-border-receiver__fetch-receivers-error"
                        title={translated.errors.somethingWentWrong}
                        description={translated.crossBorderPayment.newReceiver.errorReceiverList}
                    >
                        <Button size="small" onClick={() => refetch()}>
                            {translated.tryAgain}
                        </Button>
                    </ErrorCard>
                )}
            </div>
        </>
    );
};

export default NewCrossBorderReceiverList;
